import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Icon } from '@abw/badger-ui'

export const Terms = () =>
  <>
    <Helmet>
      <title>LFH - Terms and Conditions</title>
    </Helmet>
    <h1 className="mar-v-none">Terms and conditions</h1>
    <div className="terms about">
    <a href="/docs/LFH-TermsAndConditions-v3.0.pdf" className="large float-right">
      <Icon name="file-pdf" fixedWidth/> PDF Version
    </a>
    <h3>v3.0 (March 2021)</h3>
      <Definitions/>
      <Basis/>
      <Price/>
      <Delivery/>
      <Responsibility/>
      <Care/>
      <Loss/>
      <Termination/>
      <Default/>
      <Limitations/>
      <General/>
      <Privacy/>
    </div>
  </>

//-----------------------------------------------------------------------------
// Definitions
//-----------------------------------------------------------------------------
const Definitions = () =>
  <section>
    <h3><big>1.</big> DEFINITIONS AND INTERPRETATION</h3>
    <p>
      <big>1.1</big> In these general terms and conditions (the <code>“Terms &amp; Conditions”</code>), unless the context otherwise requires, the following expressions shall have the following meanings:
    </p>
    <div className="mar-l-4">
      <ul>
        <li><code>“Business Day”</code>        means any day other than a Sunday or a bank holiday;</li>
        <li><code>“Carrier”</code>             means a reputable carrier organisation responsible for carrying the Goods to or from the Customer;</li>
        <li><code>“Contract”</code>            means a contract which incorporates the Terms &amp; Conditions and made between the Customer and the Supplier for the hire of Goods in accordance with Condition 2;</li>
        <li><code>“Customer”</code>            means the person, firm, company or other organisation hiring Goods;</li>
        <li><code>“Deposit”</code>             means any deposit required by the Supplier in relation to the Goods which is to be held as security by the Supplier;</li>
        <li><code>“Force Majeure Event”</code> means any event outside a party's reasonable control including but not limited to acts of God, war, flood, fire, labour disputes, strikes, sub-contractors, lock-outs, riots, civil commotion, malicious damage, explosion, terrorism, governmental actions and any other similar events;</li>
        <li><code>“Goods”</code>               means any lens or camera (and/or device) together with any accessories, instructions or other accompanying documents specified in a Contract which are available for hire to a Customer;</li>
        <li><code>“Hire Period”</code>         means the period of hire of the Goods to the Customer as agreed by the parties to a Contract;</li>
        <li><code>“LFH Business Day”</code>    means a Business Day other than days between Christmas Day and New Years Day and days on which the Supplier has advised that the business will be closed;</li>
        <li><code>“Liability”</code>           means liability for any and all damages, claims, proceedings, actions, awards, expenses, costs and any other losses and/or liabilities;</li>
        <li><code>“Order Confirmation”</code>  means the written confirmation from the Supplier provided pursuant to condition 2.1;</li>
        <li><code>“Order Form”</code>          means the online transaction form identifying the details specific to an order;</li>
        <li><code>“Possession Period”</code>   means the period from the time the Customer accepts receipt of the Goods from a Carrier to the time the Customer, having returned the Goods to the Supplier, receives written confirmation that Goods have been received by the Supplier;</li>
        <li><code>“Rental”</code>              means the Supplier's charging rate for the hire of the Goods; and</li>
        <li><code>“Supplier”</code>            means Lenses for Hire Ltd and will include its employees, servants, agents and/or duly authorised representative.</li>
      </ul>
    </div>
  </section>

//-----------------------------------------------------------------------------
// Basis of Contract
//-----------------------------------------------------------------------------
const Basis = () =>
  <section>
    <h3><big>2.</big> BASIS OF CONTRACT</h3>
    <p>
      <big>2.1</big>
      Subject to circumstances arising pursuant to the rest of condition 2, a Contract binds the parties once a Customer
      receives written confirmation from the Supplier.
    </p>
    <p>
      <big>2.2</big>
      In circumstances where the Supplier has provided written confirmation pursuant to condition 2.1 but the Goods,
      for reasons outside the Supplier's control, cease to be available for the whole of the Hire Period, the Supplier
      shall inform the Customer as soon as practicable.
    </p>
    <p>
      <big>2.3</big>
      In circumstances envisaged in condition 2.2, the Supplier will use reasonable endeavours to provide suitable
      replacement equipment in order to fulfil the Contract subject always in the event of unavailability of all or
      part of an order to a Customer's right
    </p>
    <div className="mar-l-4">
      <p>
        <big>i)</big>
        to cancel the whole or part of the order and receive a full refund or a part thereof, as applicable; or
      </p>
      <p>
        <big>ii)</big>
        to re-schedule the whole or part of the order at no extra charge.
      </p>
    </div>
    <p>
      <big>2.4</big>
      Where an order for Goods, confirmed pursuant to condition 2.1, is cancelled by the Customer, the Customer
      agrees to pay a cancellation fee equal to 50% of the agreed Rental for those Goods if the cancellation occurs
      not more than one week before the start of the Hire Period, and 25% of the agreed Rental for those Goods if the
      cancellation occurs more than one week but not more than two weeks before the start of the Hire Period.
    </p>
    <p>
      <big>2.5</big>
      If a Customer, having had an order for Goods confirmed pursuant to condition 2.1, wishes to reschedule the Hire
      Period and/or vary the Goods ordered, such rescheduling may be treated by the Supplier as a cancellation and
      creation of a new order and condition 2.4 may apply.
    </p>
    <p>
      <big>2.6</big>
      Where the Goods are hired to a Customer such that the hire would be subject to the provisions the Consumer
      Credit Act 1974 (the <code>“Act”</code>) the duration of the Hire Period shall not exceed 3 months, at which
      time the Contract shall be deemed to have automatically terminated. Accordingly, a Contract for the hire of
      any Goods is not covered by the Act.
    </p>
    <p>
      <big>2.7</big>
      For the purposes of the fourteen day cancellation period pursuant to the Consumer Contracts (Information,
      Cancellation and Additional Charges) Regulations 2013, where the Hire Period will begin prior to the end of
      such cancellation period, the Customer agrees that the statutory cancellation period shall not apply.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Price and Payment
//-----------------------------------------------------------------------------
const Price = () =>
  <section>
    <h3><big>3.</big> PRICE AND PAYMENT</h3>
    <p>
      <big>3.1</big>
      The parties acknowledge that each hire transaction may be subject to a Deposit.
    </p>
    <p>
      <big>3.2</big>
      The amount of such Deposit and/or Rental shall be as quoted to the Customer or shall be as otherwise shown
      on the Supplier's web site from time to time. The Supplier's prices are quoted inclusive of any applicable VAT.
    </p>
    <p>
      <big>3.3</big>
      In respect of any applicable Deposit, the Supplier shall collect a sum equal to the Deposit from the
      Customer's credit or debit card. Such Deposit shall be returned by the Supplier as soon as practicable once
      the Supplier is satisfied that the Goods returned by the Customer are undamaged.
    </p>
    <p>
      <big>3.4</big>
      The Supplier will require payment to be authorised in full on receipt of the order.
    </p>
    <p>
      <big>3.5</big>
      The Customer shall pay the Rental, any applicable deposit, monies for outgoing and return postage and/or any
      other sums payable under the Contract (together, the <code>“Hire Charges”</code>) to the Supplier on the day
      the Goods are dispatched for the Hire Period.
    </p>
    <p>
      <big>3.6</big>
      The Customer shall pay all sums due to the Supplier under this Contract without any set-off, deduction,
      counterclaim and/or any other withholding of monies.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Delivery and Return
//-----------------------------------------------------------------------------
const Delivery = () =>
  <section>
    <h3><big>4.</big> DELIVERY AND RETURN</h3>
    <p>
      <big>4.1</big>
      Where the Order Confirmation specifies that the Customer will collect the Goods from the Supplier's
      premises, the Supplier shall make the Goods available for collection during the Supplier's normal business
      hours on the date specified in the Order Confirmation.
    </p>
    <p>
      <big>4.2</big>
      If the Customer does not collect the Goods from the Supplier in accordance with condition 4.1 on the date
      specified, the Supplier shall continue to make the Goods available for collection during normal business hours
      on each LFH Business Day until the end of the Hire Period. No adjustment to the Hire Period or the Hire Charges
      shall be occasioned by such late collection or non-collection.
    </p>
    <p>
      <big>4.3</big>
      Where the Order Confirmation specifies that the Supplier will arrange delivery of the Goods, the Supplier shall
      send the Goods to the Customer using a Carrier for delivery to the address specified in the Order Confirmation
      and on the date specified in the Order Confirmation. If the Carrier does not attempt to make delivery at the
      specified address on the specified date, the supplier shall use reasonable endeavours to arrange for the Carrier
      to deliver the Goods by midday on the next Business Day.
    </p>
    <p>
      <big>4.4</big>
      For orders consisting of multiple items, the Supplier will endeavour to send all Goods together. Should any Goods
      be unavailable for hire and the Customer consents to a reschedule of part of the Goods pursuant to condition 2.3,
      any additional costs incurred as a result of sending the Goods in multiple consignments shall be paid by the Supplier.
    </p>
    <p>
      <big>4.5</big>
      In the event that the Carrier makes its first attempt to deliver the Goods after the first day of the Hire Period,
      the Hire Period shall (subject to the availability of the Goods) only begin on the day of such delivery and shall
      continue at no additional cost to the Customer for a length of time equal to the length of the Hire Period previously
      agreed by the parties, unless other arrangements are agreed by the parties.
    </p>
    <p>
      <big>4.6</big>
      Without prejudice to condition 4.5, where a Carrier has made an attempt to deliver at the address specified in the
      Order Confirmation but the Customer has not been present to take delivery, the Hire Period shall be as previously
      agreed by the parties, unless other arrangements are agreed by the parties.
    </p>
    <p>
      <big>4.7</big>
      Without prejudice to condition 4.5, where the Customer uses facilities offered by the Carrier to divert the delivery
      to a different address, and such use entails a delay to the delivery, the Hire Period shall be as previously agreed
      by the parties, unless other arrangements are agreed by the parties.
    </p>
    <p>
      <big>4.8</big>
      The Customer shall inform the Supplier of any damaged or faulty Goods, or any missing accessories, within 24 hours of receipt.
    </p>
    <p>
      <big>4.9</big>
      Pursuant to condition 4.8 the Supplier shall make reasonable endeavours to send an identical or similar item as a
      replacement, at the Supplier's cost, for delivery by midday on the next Business Day or at the earliest opportunity
      afforded by the Carrier if such midday delivery is not possible. If notification from the Customer in accordance
      with condition 4.8 is received before 15:00 on a LFH Business Day, the Supplier shall send the replacement item
      that day; otherwise the Supplier shall send the item on the next LFH Business Day.
    </p>
    <p>
      <big>4.10</big>
      The Customer must return the Goods using the packaging provided by the Supplier, securely sealed with the Supplier's
      return address clearly marked. If the Customer does not use the Supplier's packaging, the Customer may be charged
      the cost of replacing the packaging. Any damage to the Goods due to inadequate packaging will be charged to the
      Customer at full cost.
    </p>
    <p>
      <big>4.11</big>
      Where the Order Confirmation specifies that the Customer will return the Goods in person to the Supplier's premises
      at the end of the Hire Period, the Customer shall do so during the Supplier's normal business hours, no later than
      the date specified in the Order Confirmation.
    </p>
    <p>
      <big>4.12</big>
      Where the Order Confirmation specifies that the Customer will make their own arrangements for sending the Goods
      back, the Customer shall arrange with a Carrier for the return of the Goods to the Supplier using a service which
      is tracked, insured, and (where available) scheduled for delivery on the following Business Day. The Customer shall
      send the Goods no later than the date specified in the Order Confirmation.
    </p>
    <p>
      <big>4.13</big>
      Where the Order Confirmation specifies that the Supplier will arrange for the return of the Goods, the Supplier
      shall arrange with a Carrier for the Goods to be collected from the Customer at the address specified in the Order
      Confirmation and on the date specified in the Order Confirmation.
    </p>
    <p>
      <big>4.14</big>
      Without prejudice to condition 4.16, in the event that either:
    </p>
    <div className="mar-l-4">
      <p><big>i)</big> the Goods are not returned on the correct date in accordance with condition 4.11; or</p>
      <p><big>ii)</big> the Goods are not handed to a Carrier on the correct date in accordance with condition 4.12 or 4.13; or</p>
      <p><big>iii)</big> the Goods are not returned using a next day service in accordance with condition 4.12,</p>
    </div>
    <p>
      the Customer will be charged a fee equal to up to 20% (twenty per cent) of the 7 day Rental for each complete day
      that the Goods remain unreturned or delayed.
    </p>
    <p>
      <big>4.15</big>
      Without prejudice to condition 4.14, where the Supplier has arranged a collection from the Customer in accordance
      with condition 4.13 and the customer is not available to hand over the Goods to the Carrier, the Customer may be
      charged a fee of £10 (ten pounds) plus any applicable VAT.
    </p>
    <p>
      <big>4.16</big> Where the Goods are not returned to the Supplier within 7 days following the end of the Hire
      Period and the Customer has failed to either:
    </p>
    <div className="mar-l-4">
      <p><big>i)</big> contact the Supplier and explain the circumstances of the default; or</p>
      <p><big>ii)</big> negotiate an extension to the Hire Period with the Supplier who has provided written confirmation of such an extension;</p>
    </div>
    <p>
      the Supplier will report the Goods stolen and will take steps for recovery including but not limited to charging
      the Customer's credit or debit card for the full price of replacement Goods.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Responsibility and Ownership
//-----------------------------------------------------------------------------
const Responsibility = () =>
  <section>
    <h3><big>5.</big> RESPONSIBILITY AND OWNERSHIP</h3>
    <p>
      <big>5.1</big>
      Responsibility for the Goods shall lie with the Customer for the duration of the Possession Period
      even in circumstances where the Supplier has agreed for any reason to cease charging any Rental.
    </p>
    <p>
      <big>5.2</big>
      Ownership of the Goods remains at all times with the Supplier. The Customer has no right, title or
      interest in the Goods except that they are hired to the Customer.
    </p>
    <p>
      <big>5.3</big>
      The Customer must not pass itself off as the owner of the Goods or, subject to condition 5.2,
      as having any interest in the Goods.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Care of Goods
//-----------------------------------------------------------------------------
const Care = () =>
  <section>
    <h3><big>6.</big> CARE OF GOODS</h3>
    <p>
      <big>6.1</big>
      The Customer shall take all reasonable precautions and proper measures to protect the Goods
      from loss, theft, damage and other risks.
    </p>
    <p>
      <big>6.2</big>
      The Customer shall keep the Goods at all times in its possession and control.
    </p>
    <p>
      <big>6.3</big>
      If the Customer intends to use the Goods outside the United Kingdom, the Customer shall
      notify the Supplier in advance, in writing (via online order form or email); and shall comply
      with all foreign travel advice issued by the UK Government.
    </p>
    <p>
      <big>6.4</big>
      The Customer shall notify the Supplier within 24 hours after:
    </p>
    <div className="mar-l-4">
      <p><big>i)</big> any breakdown, loss and/or damage to the Goods; or</p>
      <p><big>ii)</big> any accident resulting in damage to the Goods or other property and/or injury to any person.</p>
    </div>
    <p>
      <big>6.5</big>
      The Customer shall not continue to use Goods where they have been damaged.
    </p>
    <p>
      <big>6.6</big>
      The Customer shall not remove any labels from or interfere with the Goods, only using them for their
      proper purpose in a safe and correct manner in accordance with any operating and/or safety instructions
      supplied to the Customer.
    </p>
    <p>
      <big>6.7</big>
      The Customer shall be responsible for the conduct and cost of any testing, examinations and/or checks
      in relation to the Goods required by any legislation, best practice and/or operating instructions.
    </p>
    <p>
      <big>6.8</big>
      The Goods must be returned by the Customer in good working order and condition (fair cosmetic wear
      and tear excepted). For the avoidance of doubt any scratch or damage to glass components of the Goods
      does not qualify as fair cosmetic wear and tear.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Loss, Damage and Insurance
//-----------------------------------------------------------------------------
const Loss = () =>
  <section>
    <h3><big>7.</big> LOSS, DAMAGE AND INSURANCE</h3>
    <p>
      <big>7.1</big>
      The Rental shall include insurance for the Customer, which covers against damage or theft,
      in accordance with conditions 7.5 to 7.7.
    </p>
    <p>
      <big>7.2</big>
      Subject to condition 7.5, if the Goods are returned in a damaged, unclean or defective state,
      the Customer shall pay the Supplier for the cost of any repair or cleaning required to return
      the Goods to a condition fit for re-hire.
    </p>
    <p>
      <big>7.3</big>
      Subject to condition 7.6, the Customer shall pay the Supplier the full replacement cost for any
      Goods damaged beyond economic repair during the Possession Period.
    </p>
    <p>
      <big>7.4</big>
      Subject to condition 7.7, the Customer shall pay the Supplier the full replacement cost for any
      Goods lost, stolen, confiscated or otherwise no longer in the Customer's possession during the
      Possession Period.
    </p>
    <p>
      <big>7.5</big>
      If the Goods are returned in a damaged, unclean or defective state, and in the Supplier's reasonable
      judgement the Customer has complied with conditions 6.1 to 6.6, the Customer's payment pursuant
      to condition 7.2 shall be capped at £150 (one hundred and fifty pounds) plus any applicable VAT.
    </p>
    <p>
      <big>7.6</big>
      If the Goods are damaged beyond economic repair, and the returned Goods (in whatever condition or
      state of disrepair) are clearly and indisputably identifiable as the Supplier's Goods by way of the
      manufacturers serial number or any other unique identifier indexed and recorded by the Supplier, and
      in the Supplier's reasonable judgement the Customer has complied with conditions 6.1 to 6.6, the
      Customer's payment pursuant to condition 7.3 shall be capped at £150 (one hundred and fifty pounds)
      plus any applicable VAT.
    </p>
    <p>
      <big>7.7</big>
      If the Goods are stolen from the customer, and the customer reports the theft as soon as reasonably
      possible to the police and provides the Supplier with details of the police report and the crime
      reference number, and in the Supplier's reasonable judgement the Customer has complied with conditions
      6.1 to 6.6, the Customer's payment pursuant to condition 7.3 shall be capped at £250 (two hundred and
      fifty pounds) plus any applicable VAT.
    </p>
    <p>
      <big>7.8</big>
      The Customer agrees that failure to adhere to all of the conditions stipulated in conditions 7.5 to 7.7
      will make the Customer liable to pay for the full cost of repair or replacement of the Goods pursuant to
      conditions 7.2 to 7.4.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Termination
//-----------------------------------------------------------------------------
const Termination = () =>
  <section>
    <h3><big>8.</big> TERMINATION</h3>
    <p>
      <big>8.1</big>
      Subject always to condition 9 neither the Customer nor the Supplier shall be entitled to terminate a
      Contract before the expiry of the Hire Period unless agreed with the other party.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Default
//-----------------------------------------------------------------------------
const Default = () =>
  <section>
    <h3><big>9.</big> DEFAULT</h3>
    <p><big>9.1</big> If the Customer:-</p>
    <div className="mar-l-4">
      <p>
        <big>9.1.1</big>
        fails to make any payment to the Supplier when due without just cause;
      </p>
      <p>
        <big>9.1.2</big>
        breaches the terms of the Contract and, where the breach is capable of remedy, has
        not remedied the breach within 24 hours of receiving notice requiring the breach to be remedied;
      </p>
      <p>
        <big>9.1.4</big>
        provides incomplete, materially inaccurate or misleading facts and/or information in connection with the Contract;
      </p>
      <p>
        <big>9.1.3</big>
        persistently breaches the terms of the Contract;
      </p>
      <p>
        <big>9.1.5</big>
        pledges, charges or creates any form of security over any Goods, or ceases or
        threatens to cease to carry on business, or proposes to compound with its creditors,
        applies for an interim moratorium in respect of claims and/or proceedings, any distress,
        execution or other legal process is levied on any property of the Customer, has a
        Bankruptcy Petition presented against it or the Customer takes or suffers any similar
        action in any jurisdiction;
      </p>
      <p>
        <big>9.1.6</big>
        being a company, enters into voluntary or compulsory liquidation, has a receiver,
        administrator or administrative receiver appointed over all or any of its assets, any
        attachment order is made against the Customer, any distress, execution or other legal
        process is levied on any property of the Customer or the Customer takes or suffers any
        similar action in any jurisdiction;
      </p>
      <p>
        <big>9.1.7</big>
        appears reasonably to the Supplier to be financially inadequate to meet its obligations
        under the Contract; and/or
      </p>
      <p>
        <big>9.1.8</big>
        appears reasonably to the Supplier to be about to suffer any of the above events,
      </p>
    </div>
    <p>
      then the Supplier shall have the right, without prejudice to any other remedies, to exercise
      any or all of the rights set out in condition 9.2.
    </p>
    <p>
      <big>9.2</big> If any of the events set out in condition 9.1 occurs in relation to the Customer then:-
    </p>
    <div className="mar-l-4">
      <p>
        <big>9.2.1</big>
        so far as the law permits, the Supplier may enter, without prior notice, any premises of the
        Customer where Goods owned by the Supplier may be and repossess any Goods;
      </p>
      <p>
        <big>9.2.2</big>
        the Supplier may immediately cancel, terminate and/or suspend without Liability to the Customer
        the Contract and/or any other contract with the Customer; and/or
      </p>
      <p>
        <big>9.2.3</big>
        all monies owed by the Customer to the Supplier shall immediately become due and payable.
      </p>
    </div>
    <p>
      <big>9.3</big>
      Any repossession of the Goods envisaged under condition 9.2 shall not affect the Supplier's
      right to recover from the Customer any monies due under the Contract and/or any damages in
      respect of any breach which occurred prior to repossession of the Goods.
    </p>
    <p>
      <big>9.4</big>
      Upon termination of the Contract the Customer shall immediately return the Goods in accordance
      with conditions 4.11 or 4.12, or make the Goods available for collection by the Supplier, as
      requested by the Supplier.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Limitations of Liability
//-----------------------------------------------------------------------------
const Limitations = () =>
  <section>
    <h3><big>10.</big> LIMITATIONS OF LIABILITY</h3>
    <p>
      <big>10.1</big>
      All warranties, representations, terms, conditions and duties implied by law relating to
      fitness, quality and/or adequacy are excluded to the fullest extent permitted by law.
    </p>
    <p>
      <big>10.2</big>
      The Supplier shall have no Liability to the Customer if, without just cause, any monies
      due in respect of the Goods has not been paid in full by the due date for payment.
    </p>
    <p>
      <big>10.3</big>
      The Supplier shall have no Liability for additional damage, loss, consequential loss,
      liability, claims, costs or expenses caused or contributed to by the Customer's continued
      use of defective Goods after a defect has become apparent or suspected or should reasonably
      have become apparent to the Customer.
    </p>
    <p>
      <big>10.4</big>
      The Customer shall give the Supplier a reasonable opportunity to remedy any matter for which
      the Supplier is liable before the Customer incurs any costs and/or expenses in remedying the
      matter itself. If the Customer does not do so the Supplier shall have no Liability to the Customer.
    </p>
    <p>
      <big>10.5</big>
      The Supplier's total Liability to the Customer for:
    </p>
    <div className="mar-l-4">
      <p><big>10.5.1</big> breach of contract;</p>
      <p><big>10.5.2</big> tort (including negligence); and</p>
      <p><big>10.5.3</big> breach of statutory and/or common law duty, arising from or in connection with any Contract</p>
    </div>
    <p>
      shall not cumulatively exceed the amount of the Hire Charges under such Contract or £100
      (one hundred pounds) whichever is the higher. To the extent that any Liability of the Supplier
      would be met by any insurance of the Customer, the Liability of the Supplier under a Contract
      shall be reduced by the amount paid out to the Customer by its insurer.
    </p>
    <p>
      <big>10.6</big>
      Nothing in this Contract shall exclude or limit the Liability of the Supplier for death or personal
      injury due to the Supplier's negligence.
    </p>
  </section>

//-----------------------------------------------------------------------------
// General
//-----------------------------------------------------------------------------
const General = () =>
  <section>
    <h3><big>11.</big> GENERAL</h3>
    <p>
      <big>11.1</big>
      Upon termination of the Contract the applicable provisions of conditions 3, 5 and 7 shall continue
      in full force and effect.
    </p>
    <p>
      <big>11.2</big>
      Each hire of an item of Goods shall form a distinct Contract which shall be separate to any other
      Contract relating to other Goods.
    </p>
    <p>
      <big>11.3</big>
      No variation to the Contract shall be valid unless it is in writing and signed by or on behalf
      of both the Customer and the Supplier by a duly authorised officer of the parties.
    </p>
    <p>
      <big>11.4</big>
      The Customer shall not, or purport to, assign its rights or transfer or subcontract any of its
      rights or obligations under the Contract without the prior written consent of the Supplier.
    </p>
    <p>
      <big>11.5</big>
      The Customer shall be liable, where applicable, for the acts and/or omissions of its employees,
      agents, servants and/or subcontractors as though they were its own acts and/or omissions under
      this Contract.
    </p>
    <p>
      <big>11.6</big>
      The Customer agrees to indemnify and keep indemnified the Supplier against any and all losses,
      lost profits, damages, claims, costs (including legal costs on a full indemnity basis), actions
      and any other losses and/or liabilities suffered by the Supplier and arising from or due to any
      breach of contract, any tortious act and/or omission and/or any breach of statutory duty by the Customer.
    </p>
    <p>
      <big>11.7</big>
      No waiver by the Supplier of any breach of this Contract shall be considered as a waiver of any
      subsequent breach of the same provision or any other provision.
    </p>
    <p>
      <big>11.8</big>
      If any provision is held by any competent authority to be unenforceable in whole or in part the
      validity of the other provisions of this Contract and the remainder of the affected provision shall
      be unaffected and shall remain in full force and effect.
    </p>
    <p>
      <big>11.9</big>
      The Supplier shall have no Liability to the Customer for any delay and/or non performance of a Contract
      to the extent that such delay or non-performance is due to any Force Majeure Event.
    </p>
    <p>
      <big>11.10</big>
      These Terms and Conditions together with the other agreed terms comprising the Contract and any document
      expressly referred to in any of its terms contains the entire agreement between the Customer and the
      Supplier relating to the subject matter covered and supersedes any previous agreements, arrangements,
      undertakings or proposals, written or oral, in relation to such matters. No oral explanation or oral
      information given by either the Customer or the Supplier shall alter the interpretation of the
      Terms &amp; Conditions or the Contract.
    </p>
    <p>
      <big>11.11</big>
      The Customer agrees that the only remedy available to it for breach of any term expressly set out in
      the Contract shall be a breach of contract. Nothing contained in this condition 11.11 shall however
      operate to limit or exclude any liability of either the Customer or the Supplier for fraud or fraudulent
      misrepresentation.
    </p>
    <p>
      <big>11.12</big>
      Subject to condition 11.13, all third party rights are excluded and no third parties shall have any
      rights to enforce the Contract.
    </p>
    <p>
      <big>11.13</big>
      Without prejudice to the generality of condition 11.12 a finance company with whom the Supplier has
      an outstanding finance agreement relating to the Goods shall, subject to the Supplier's consent, have
      the right to enforce this Contract as if they were the Supplier.
    </p>
    <p>
      <big>11.14</big>
      This Contract is governed by and interpreted in accordance with English law and the parties agree to
      submit to the non-exclusive jurisdiction of the English courts.
    </p>
  </section>

//-----------------------------------------------------------------------------
// Privacy
//-----------------------------------------------------------------------------
const Privacy = () =>
  <section>
    <h3><big>12.</big> PRIVACY</h3>
    <p>
      <big>12.1</big>
      All information gathered or received by the Supplier will be regarded as private and
      will not be shared with any third party, apart from the exceptions set out in conditions 12.2 to 12.6.
    </p>
    <p>
      <big>12.2</big>
      When the Customer places an order via the Supplier's web site, certain information such as name,
      address, email address, and payment card details is captured and processed by the third party operating
      a payment gateway on behalf of the Supplier. Such processing may include communication with the Customer's
      payment card issuer and other industry-wide databases.
    </p>
    <p>
      <big>12.3</big>
      The Supplier may share the Customer's contact information and order history with the Supplier's accountants.
    </p>
    <p>
      <big>12.4</big>
      Where the Supplier is required to arrange delivery of the Goods pursuant to condition 4.3, or collection
      pursuant to condition 4.13, the Supplier may share the Customer's contact information with the Carrier.
    </p>
    <p>
      <big>12.5</big>
      Where the Customer owes monies pursuant to condition 9.1.1, the Supplier may share any information about
      the Customer in its possession with the Supplier's debt collection agents. They may in turn pass this
      information on to their legal advisers and/or officers of the court when necessary.
    </p>
    <p>
      <big>12.6</big>
      Where the Customer fails to return the Goods or commits a serious breach of contract, the Supplier may
      share any information about the Customer in its possession with the Supplier's solicitors, relevant law
      enforcement agencies, insurance companies, and other hire companies within the X-hire Anti-Fraud Network.
    </p>
  </section>

export default Terms